import React, { useState, useEffect, useRef } from 'react';
import { chatApi, getAuthSession } from '../services/api';
import { formatWhatsAppPhone } from '../utils/whatsapp';
import useSocket from '../hooks/useSocket';

// Helper function to extract real phone numbers consistently from any contact object shape
const getContactPhone = (contact) => {
  if (!contact) return '';
  if (typeof contact === 'string' && /^\+?\d{7,15}$/.test(contact)) return contact;
  return (
    contact.phone ||
    contact.clientPhone ||
    contact.therapistPhone ||
    contact.userId?.phone ||
    contact.client?.phone ||
    contact.therapist?.phone ||
    contact.clientId?.phone ||
    contact.therapistId?.phone ||
    (Array.isArray(contact.participants) && contact.participants.find(p => p && p.phone)?.phone) ||
    ''
  );
};

// Helper function to extract user ID consistently from any contact object shape
const getRecipientId = (contact) => {
  if (!contact) return null;
  if (typeof contact === 'string') return contact;
  if (contact.userId) {
    if (typeof contact.userId === 'object') return String(contact.userId._id || contact.userId.id || '');
    return String(contact.userId);
  }
  if (contact._id) return String(contact._id);
  if (contact.id) return String(contact.id);
  return null;
};

// Format timestamp for WhatsApp chat bubbles & list previews
const formatMessageTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const InbuiltWhatsAppModal = ({ isOpen, onClose, targetUser, contactsList = [], initialMessage = '' }) => {
  const authSession = getAuthSession();
  const currentUserId = authSession?.user?._id || authSession?.user?.id;

  const [activeContact, setActiveContact] = useState(targetUser || null);
  const [isSessionCompleted, setIsSessionCompleted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatsList, setChatsList] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);

  const messagesEndRef = useRef(null);
  const currentChatIdRef = useRef(null);
  const inputRef = useRef(null);

  const { subscribeToChat, onChatMessage } = useSocket();

  // Listen for real-time messages (including inbound WhatsApp webhook replies)
  useEffect(() => {
    if (currentChatIdRef.current) {
      subscribeToChat(currentChatIdRef.current);
    }
  }, [currentChatIdRef.current, subscribeToChat]);

  useEffect(() => {
    onChatMessage((data) => {
      const incomingChatId = data?.chatId || data?.message?.chatId;
      if (incomingChatId && currentChatIdRef.current && String(incomingChatId) === String(currentChatIdRef.current)) {
        const incomingMsg = data?.message || data;
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(incomingMsg._id))) return prev;
          return [...prev, incomingMsg];
        });
      }
    });
  }, [onChatMessage]);

  // Synchronize targetUser prop & initialMessage when modal opens
  useEffect(() => {
    if (targetUser) {
      setActiveContact(targetUser);
      setIsSessionCompleted(Boolean(targetUser.isSessionCompleted || targetUser.status === 'completed'));
      currentChatIdRef.current = null;
      setShowMobileSidebar(false);
    }
    if (initialMessage) {
      setInputText(initialMessage);
    }
  }, [targetUser, isOpen, initialMessage]);

  // Update session completed state whenever activeContact changes
  useEffect(() => {
    if (activeContact) {
      setIsSessionCompleted(Boolean(activeContact.isSessionCompleted || activeContact.status === 'completed'));
    }
  }, [activeContact]);

  // Focus input when active contact changes (if active contact session is not completed)
  useEffect(() => {
    if (activeContact && isOpen && !isSessionCompleted) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [activeContact, isOpen, isSessionCompleted]);

  // Load existing chats list
  const loadChats = async () => {
    try {
      const res = await chatApi.getChats();
      const rawChats = res.chats || res.data || [];
      setChatsList(rawChats);
    } catch (err) {
      console.error('Failed to load chats list:', err);
    }
  };

  // Load messages for active contact
  const loadMessagesForContact = async (contact, skipChatsListFetch = false) => {
    if (!contact) return;
    const recipientId = getRecipientId(contact);
    if (!recipientId) return;

    if (!skipChatsListFetch && messages.length === 0) {
      setLoadingMessages(true);
    }

    try {
      let targetChatId = currentChatIdRef.current;

      if (!targetChatId || !skipChatsListFetch) {
        const res = await chatApi.getChats();
        const chats = res.chats || [];
        setChatsList(chats);

        const foundChat = chats.find(c => 
          c.participants && c.participants.some(p => {
            const pid = typeof p === 'object' ? String(p._id || p.id || '') : String(p);
            return pid === String(recipientId);
          })
        );

        if (foundChat) {
          targetChatId = foundChat._id;
          currentChatIdRef.current = foundChat._id;
          if (foundChat.isSessionCompleted !== undefined) {
            setIsSessionCompleted(Boolean(foundChat.isSessionCompleted));
          }
        }
      }

      if (targetChatId) {
        const msgRes = await chatApi.getMessages(targetChatId);
        setMessages(msgRes.messages || []);
        if (msgRes.isSessionCompleted !== undefined) {
          setIsSessionCompleted(Boolean(msgRes.isSessionCompleted));
        }
        chatApi.markRead(targetChatId).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadChats();
      if (activeContact) {
        loadMessagesForContact(activeContact, false);
      }
    }
  }, [isOpen, activeContact]);

  // Poll active chat messages every 4 seconds without re-fetching full chat list
  useEffect(() => {
    if (!isOpen || !activeContact) return;
    const interval = setInterval(() => {
      loadMessagesForContact(activeContact, true);
    }, 4000);
    return () => clearInterval(interval);
  }, [isOpen, activeContact]);

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (textToSend) => {
    if (isSessionCompleted) {
      alert('Session is completed. You cannot send messages through inbuilt WhatsApp once the session is complete.');
      return;
    }

    const content = textToSend || inputText;
    if (!content || !content.trim() || !activeContact || sending) return;

    const recipientId = getRecipientId(activeContact);
    if (!recipientId) {
      alert('Unable to identify recipient ID for this contact. Please select a valid contact.');
      return;
    }

    setSending(true);
    setInputText('');

    // Optimistic UI message
    const tempMsg = {
      _id: 'temp-' + Date.now(),
      sender: currentUserId,
      content: content.trim(),
      sentAt: new Date().toISOString(),
      pending: true
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await chatApi.sendMessage(recipientId, content.trim());
      if (res.chat?._id) {
        currentChatIdRef.current = res.chat._id;
      }
      await loadMessagesForContact(activeContact, true);
      loadChats().catch(() => {});
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove optimistic temp message
      setMessages(prev => prev.filter(m => m._id !== tempMsg._id));
      if (err.message && (err.message.includes('completed') || err.message.includes('Forbidden'))) {
        setIsSessionCompleted(true);
      }
      alert('Failed to send message: ' + (err.message || 'Server error'));
    } finally {
      setSending(false);
    }
  };

  // Send message in software DB AND launch real WhatsApp app with prefilled text
  const handleSendAndOpenWhatsAppApp = async (customText) => {
    const textToSend = customText || inputText;
    if (!textToSend || !textToSend.trim() || !activeContact) return;
    const content = textToSend.trim();
    const rawPhone = getContactPhone(activeContact);
    
    // 1. Save to software DB so chat is stored & visible in software
    await handleSendMessage(content);

    // 2. Open native WhatsApp app with real phone number & prefilled message
    if (rawPhone) {
      const formatted = formatWhatsAppPhone(rawPhone);
      const encoded = encodeURIComponent(content);
      const waUrl = `https://wa.me/${formatted}?text=${encoded}`;
      window.open(waUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Download Chat transcript as a formatted text file
  const handleDownloadChat = () => {
    if (!messages || messages.length === 0) {
      alert('No chat messages available to download.');
      return;
    }
    const contactName = activeContact?.name || activeContact?.clientName || activeContact?.therapistName || 'Client';
    let textContent = `====================================================\n`;
    textContent += `PSYCHMITRA THERAPY SESSION CHAT TRANSCRIPT\n`;
    textContent += `Participant: ${contactName}\n`;
    textContent += `Export Date: ${new Date().toLocaleString()}\n`;
    textContent += `====================================================\n\n`;

    messages.forEach(msg => {
      const isMe = String(msg.sender?._id || msg.sender) === String(currentUserId);
      const senderName = isMe ? 'Therapist' : contactName;
      const timeStr = msg.sentAt ? new Date(msg.sentAt).toLocaleString() : '';
      textContent += `[${timeStr}] ${senderName}: ${msg.content || ''}\n`;
    });

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Chat_Transcript_${contactName.replace(/\s+/g, '_')}_${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Send Chat transcript directly to Client in internal messaging
  const handleSendChatToClient = async () => {
    if (!messages || messages.length === 0) {
      alert('No chat messages available to send.');
      return;
    }
    const recipientId = getRecipientId(activeContact);
    if (!recipientId) {
      alert('Unable to identify client recipient ID.');
      return;
    }

    const contactName = activeContact?.name || activeContact?.clientName || activeContact?.therapistName || 'Client';
    let transcriptText = `📄 **Session Chat Transcript**\n\n`;
    messages.forEach(msg => {
      const isMe = String(msg.sender?._id || msg.sender) === String(currentUserId);
      const senderName = isMe ? 'Therapist' : contactName;
      const timeStr = msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
      transcriptText += `[${timeStr}] ${senderName}: ${msg.content || ''}\n`;
    });

    try {
      setSending(true);
      await chatApi.sendMessage(recipientId, transcriptText);
      alert(`Chat transcript successfully sent to ${contactName}!`);
      await loadMessagesForContact(activeContact, true);
    } catch (err) {
      alert('Failed to send chat transcript: ' + (err.message || 'Server error'));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  // Prepare merged contacts list
  const allContactsMap = new Map();

  if (targetUser) {
    const id = targetUser._id || targetUser.id || targetUser.userId?._id;
    if (id) allContactsMap.set(String(id), targetUser);
  }

  contactsList.forEach(c => {
    const id = c._id || c.id || c.userId?._id;
    if (id) allContactsMap.set(String(id), c);
  });

  chatsList.forEach(chat => {
    if (chat.participants) {
      chat.participants.forEach(p => {
        if (typeof p === 'object' && String(p._id) !== String(currentUserId)) {
          if (!allContactsMap.has(String(p._id))) {
            allContactsMap.set(String(p._id), p);
          }
        }
      });
    }
  });

  const mergedContactsList = Array.from(allContactsMap.values()).filter(c => {
    const name = c.name || c.clientName || c.therapistName || '';
    const phone = c.phone || c.clientPhone || c.therapistPhone || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase()) || phone.includes(searchQuery);
  });

  const quickReplies = [
    '👋 Hello! Ready for our session.',
    '📹 Can we connect via video call?',
    '📋 Please share the prescription notes.',
    '👍 Thank you!'
  ];

  return (
    <div className="wa-modal-overlay">
      <style>{`
        .wa-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(11, 20, 26, 0.75);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 16px;
          animation: waFadeIn 0.25s ease-out;
        }

        @keyframes waFadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }

        .wa-modal-card {
          width: 1050px;
          max-width: 98vw;
          height: 680px;
          max-height: 92vh;
          background-color: #ffffff;
          border-radius: 16px;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.4);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.1);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        /* Top Header */
        .wa-top-header {
          background: linear-gradient(135deg, #075E54 0%, #008069 100%);
          color: #ffffff;
          padding: 14px 22px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        /* Sidebar contacts */
        .wa-sidebar {
          width: 320px;
          border-right: 1px solid #e9edef;
          background-color: #ffffff;
          display: flex;
          flex-direction: column;
          transition: all 0.3s ease;
        }

        .wa-contact-item {
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          border-bottom: 1px solid #f0f2f5;
          transition: background-color 0.15s ease;
          position: relative;
        }

        .wa-contact-item:hover {
          background-color: #f5f6f6;
        }

        .wa-contact-item.active {
          background-color: #f0f2f5;
          border-left: 4px solid #00a884;
        }

        /* Chat wall */
        .wa-chat-wall {
          flex: 1;
          display: flex;
          flex-direction: column;
          background-color: #efeae2;
          background-image: radial-gradient(#d1d7db 1px, transparent 1px);
          background-size: 20px 20px;
          position: relative;
        }

        .wa-messages-list {
          flex: 1;
          padding: 20px 24px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* Scrollbars */
        .wa-messages-list::-webkit-scrollbar,
        .wa-contacts-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .wa-messages-list::-webkit-scrollbar-thumb,
        .wa-contacts-scroll::-webkit-scrollbar-thumb {
          background: #cccccc;
          border-radius: 4px;
        }

        /* Quick Reply Badges */
        .wa-chip {
          padding: 6px 14px;
          background-color: #ffffff;
          border: 1px solid #d1d7db;
          border-radius: 18px;
          font-size: 12.5px;
          color: #111b21;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
        }

        .wa-chip:hover {
          background-color: #00a884;
          color: #ffffff;
          border-color: #00a884;
        }

        /* Send button */
        .wa-send-btn {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background-color: #00a884;
          color: #ffffff;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.15s ease, background-color 0.15s ease;
          box-shadow: 0 2px 5px rgba(0, 168, 132, 0.4);
        }
        .wa-send-btn:hover:not(:disabled) {
          background-color: #008f70;
          transform: scale(1.05);
        }
        .wa-send-btn:disabled {
          background-color: #cccccc;
          box-shadow: none;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .wa-sidebar {
            width: 100%;
            display: ${showMobileSidebar ? 'flex' : 'none'};
          }
          .wa-chat-wall {
            display: ${showMobileSidebar ? 'none' : 'flex'};
          }
          .wa-mobile-back {
            display: block !important;
          }
        }
      `}</style>

      <div className="wa-modal-card">
        {/* TOP MAIN HEADER */}
        <div className="wa-top-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* WhatsApp Logo SVG */}
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              backgroundColor: '#25D366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(37,211,102,0.4)'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#ffffff">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-0.999 3.648 3.742-0.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
              </svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, letterSpacing: '0.2px' }}>
                PsychMitra WhatsApp Messenger
              </h3>
              <span style={{ fontSize: '12px', color: '#e6fffa', opacity: 0.9 }}>
                Direct Real-time Patient-Therapist Communication Portal
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* WhatsApp Business Live Status Badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                padding: '6px 14px',
                borderRadius: '24px',
                fontSize: '11.5px',
                fontWeight: 600,
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.25)',
              }}
              title="WhatsApp Business API (ChatMitra Integration Active)"
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#25D366',
                  display: 'inline-block',
                  boxShadow: '0 0 8px #25D366',
                }}
              ></span>
              <span>ChatMitra Live (+91 89830 82442)</span>
            </div>

            <button
              onClick={onClose}
              title="Close chat"
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: '#ffffff',
                fontSize: '18px',
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* MAIN BODY: SIDEBAR + CHAT AREA */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* LEFT SIDEBAR: CONTACTS */}
          <div className="wa-sidebar">
            {/* Search Box */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9edef', backgroundColor: '#ffffff' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f0f2f5',
                borderRadius: '8px',
                padding: '6px 12px',
                gap: '8px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#54656f" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '13.5px',
                    outline: 'none',
                    color: '#111b21'
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#54656f', padding: 0 }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Contacts List */}
            <div className="wa-contacts-scroll" style={{ flex: 1, overflowY: 'auto' }}>
              {mergedContactsList.length > 0 ? (
                mergedContactsList.map(contact => {
                  const contactId = contact._id || contact.id || contact.userId?._id;
                  const activeId = activeContact?._id || activeContact?.id || activeContact?.userId?._id;
                  const isSelected = String(contactId) === String(activeId);
                  const name = contact.name || contact.clientName || contact.therapistName || 'User';
                  const role = contact.role || (contact.specialization ? 'therapist' : 'client');
                  const phone = contact.phone || contact.clientPhone || contact.therapistPhone || '';

                  return (
                    <div
                      key={contactId}
                      className={`wa-contact-item ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        setActiveContact(contact);
                        setShowMobileSidebar(false);
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        backgroundColor: role.toLowerCase() === 'therapist' ? '#0284c7' : '#059669',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '17px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {name.charAt(0).toUpperCase()}
                        <span style={{
                          position: 'absolute',
                          bottom: '1px',
                          right: '1px',
                          width: '11px',
                          height: '11px',
                          backgroundColor: '#22c55e',
                          borderRadius: '50%',
                          border: '2px solid #ffffff'
                        }}></span>
                      </div>

                      {/* Contact Info */}
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{
                            margin: 0,
                            fontSize: '14.5px',
                            fontWeight: 600,
                            color: '#111b21',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {name}
                          </h4>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: '4px',
                            backgroundColor: role.toLowerCase() === 'therapist' ? '#e0f2fe' : '#dcfce7',
                            color: role.toLowerCase() === 'therapist' ? '#0369a1' : '#15803d',
                            textTransform: 'uppercase'
                          }}>
                            {role}
                          </span>
                          {(contact.isSessionCompleted || contact.status === 'completed') && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: '4px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              textTransform: 'uppercase'
                            }}>
                              🔒 Closed
                            </span>
                          )}
                          {phone && (
                            <span style={{ fontSize: '11.5px', color: '#667781', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              • {phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '30px 20px', textAlign: 'center', color: '#667781', fontSize: '13.5px' }}>
                  No contacts found
                </div>
              )}
            </div>
          </div>

          {/* RIGHT CHAT AREA */}
          <div className="wa-chat-wall">
            {activeContact ? (
              <>
                {/* CHAT HEADER */}
                <div style={{
                  padding: '10px 20px',
                  backgroundColor: '#f0f2f5',
                  borderBottom: '1px solid #d1d7db',
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Back button for mobile view */}
                    <button
                      onClick={() => setShowMobileSidebar(true)}
                      style={{
                        border: 'none',
                        background: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        display: 'none',
                        color: '#54656f'
                      }}
                      className="wa-mobile-back"
                    >
                      ←
                    </button>

                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#075e54',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {(activeContact.name || activeContact.clientName || activeContact.therapistName || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111b21' }}>
                        {activeContact.name || activeContact.clientName || activeContact.therapistName}
                      </h4>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#00a884', fontWeight: 500 }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#00a884', display: 'inline-block' }}></span>
                        <span>
                          📱 {getContactPhone(activeContact) ? `WhatsApp (+${formatWhatsAppPhone(getContactPhone(activeContact))})` : 'Real WhatsApp Connected'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons header section */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleDownloadChat}
                      title="Download chat history"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        border: '1px solid #cbd5e1',
                        padding: '6px 13px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      📥 Download Chat
                    </button>

                    <button
                      onClick={handleSendChatToClient}
                      title="Send chat transcript to client"
                      style={{
                        backgroundColor: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 13px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 6px rgba(2,132,199,0.3)'
                      }}
                    >
                      📩 Send Chat to Client
                    </button>

                    {/* External WhatsApp button with real WhatsApp phone number */}
                    {getContactPhone(activeContact) && (
                      <a
                        href={`https://wa.me/${formatWhatsAppPhone(getContactPhone(activeContact))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open chat in official WhatsApp app"
                        style={{
                          backgroundColor: '#25D366',
                          color: '#ffffff',
                          padding: '6px 13px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 700,
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 6px rgba(37,211,102,0.35)',
                          transition: 'transform 0.15s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                        onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#ffffff">
                          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-0.999 3.648 3.742-0.981z"/>
                        </svg>
                        📱 Open WhatsApp App (+{formatWhatsAppPhone(getContactPhone(activeContact))})
                      </a>
                    )}
                  </div>
                </div>

                {/* SESSION COMPLETED WARNING BANNER */}
                {isSessionCompleted && (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    borderBottom: '1px solid #ffeeba',
                    padding: '10px 18px',
                    fontSize: '13px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <span>🔒</span> Session Completed: Inbuilt WhatsApp messaging is closed for completed sessions. Past chat history remains available to view and download.
                  </div>
                )}

                {/* MESSAGES LIST */}
                <div className="wa-messages-list">
                  {/* Date Pill */}
                  <div style={{ textAlign: 'center', margin: '8px 0 14px 0' }}>
                    <span style={{
                      backgroundColor: '#ffffff',
                      color: '#54656f',
                      padding: '5px 14px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: 600,
                      boxShadow: '0 1px 2px rgba(11,20,26,0.12)',
                      letterSpacing: '0.3px'
                    }}>
                      TODAY • SECURE INBUILT WHATSAPP SESSION
                    </span>
                  </div>

                  {loadingMessages && messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#54656f', margin: 'auto', fontSize: '13.5px' }}>
                      Loading messages...
                    </div>
                  ) : messages.length > 0 ? (
                    messages.map((msg, idx) => {
                      const isMe = String(msg.sender?._id || msg.sender) === String(currentUserId);
                      const timeStr = formatMessageTime(msg.sentAt);

                      return (
                        <div
                          key={msg._id || idx}
                          style={{
                            alignSelf: isMe ? 'flex-end' : 'flex-start',
                            maxWidth: '72%',
                            backgroundColor: isMe ? '#d9fdd3' : '#ffffff',
                            color: '#111b21',
                            padding: '8px 12px 6px 12px',
                            borderRadius: isMe ? '10px 10px 0px 10px' : '10px 10px 10px 0px',
                            boxShadow: '0 1px 2px rgba(11, 20, 26, 0.12)',
                            wordBreak: 'break-word',
                            fontSize: '14px',
                            lineHeight: '1.45',
                            position: 'relative'
                          }}
                        >
                          <div>{msg.content}</div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '4px',
                            marginTop: '2px',
                            fontSize: '10.5px',
                            color: '#667781'
                          }}>
                            {msg.source === 'whatsapp' ? (
                              <span style={{ fontSize: '10px', color: '#059669', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                📱 via WhatsApp
                              </span>
                            ) : (
                              <span style={{ fontSize: '10px', color: '#059669', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                💬 WhatsApp Synced
                              </span>
                            )}
                            <span>{timeStr || 'Just now'}</span>
                            {isMe && (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#53bdeb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                                <polyline points="20 12 13.5 18.5"></polyline>
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#54656f',
                      margin: 'auto',
                      background: 'rgba(255,255,255,0.92)',
                      padding: '24px 32px',
                      borderRadius: '16px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                      maxWidth: '360px'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>💬</div>
                      <h4 style={{ margin: '0 0 6px 0', color: '#111b21', fontSize: '15px' }}>No messages yet</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#667781', lineHeight: '1.4' }}>
                        Start the conversation below or select a quick response to send a message.
                      </p>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* QUICK REPLIES BAR */}
                <div style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0f2f5',
                  borderTop: '1px solid #e9edef',
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  opacity: isSessionCompleted ? 0.6 : 1
                }}>
                  {quickReplies.map((reply, i) => (
                    <button
                      key={i}
                      className="wa-chip"
                      disabled={isSessionCompleted}
                      onClick={() => !isSessionCompleted && handleSendMessage(reply)}
                      style={{ cursor: isSessionCompleted ? 'not-allowed' : 'pointer' }}
                    >
                      {reply}
                    </button>
                  ))}
                </div>

                {/* MESSAGE INPUT FORM */}
                <form
                  onSubmit={(e) => { e.preventDefault(); if (!isSessionCompleted) handleSendMessage(); }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#f0f2f5',
                    borderTop: '1px solid #d1d7db',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={isSessionCompleted ? "Session completed. Messaging is disabled." : "Type a message..."}
                    value={inputText}
                    disabled={isSessionCompleted}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isSessionCompleted) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '11px 16px',
                      borderRadius: '20px',
                      border: '1px solid #ffffff',
                      fontSize: '14px',
                      backgroundColor: isSessionCompleted ? '#e2e8f0' : '#ffffff',
                      outline: 'none',
                      color: isSessionCompleted ? '#64748b' : '#111b21',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      cursor: isSessionCompleted ? 'not-allowed' : 'text'
                    }}
                  />

                  <button
                    type="submit"
                    className="wa-send-btn"
                    disabled={sending || !inputText.trim() || isSessionCompleted}
                    title={isSessionCompleted ? "Session completed" : "Send message in software DB"}
                    style={{ background: '#00a884', color: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  >
                    {sending ? (
                      <span style={{ fontSize: '12px' }}>⏳</span>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '2px' }}>
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSendAndOpenWhatsAppApp()}
                    disabled={sending || !inputText.trim() || isSessionCompleted}
                    title="Send in Software & Open Native WhatsApp App (wa.me)"
                    style={{
                      background: '#25D366',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: isSessionCompleted || !inputText.trim() ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      boxShadow: '0 2px 6px rgba(37,211,102,0.35)',
                      opacity: isSessionCompleted || !inputText.trim() ? 0.5 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    🚀 Send & Launch WhatsApp App
                  </button>
                </form>
              </>
            ) : (
              <div style={{ margin: 'auto', textAlign: 'center', color: '#54656f', padding: '20px' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="#00a884">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-0.999 3.648 3.742-0.981z"/>
                  </svg>
                </div>
                <h3 style={{ margin: '0 0 8px 0', color: '#111b21', fontSize: '18px', fontWeight: 600 }}>
                  PsychMitra WhatsApp Chat
                </h3>
                <p style={{ margin: 0, fontSize: '13.5px', color: '#667781', maxWidth: '300px' }}>
                  Select a contact from the list on the left to start viewing and sending real-time messages.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InbuiltWhatsAppModal;
